/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import axiosInstance from '../axios.config';
import API_ENDPOINTS from '../endpoints';

export const authService = {
  // Login
  login: async (credentials) => {
    console.log('🔐 [AuthService] Starting login with:', { email: credentials.email });
    console.log('🔐 [AuthService] API endpoint:', API_ENDPOINTS.AUTH.LOGIN);
    
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
      
      console.log('🔐 [AuthService] Raw response:', response);
      console.log('🔐 [AuthService] Response status:', response?.status);
      console.log('🔐 [AuthService] Response data:', response?.data);
      
      if (response && response.status === 'success' && response.data) {
        // Backend returns: { accessToken, refreshToken, expiresIn, user }
        const { accessToken, refreshToken, user } = response.data;
        
        console.log('🔐 [AuthService] Extracted tokens:', { 
          hasAccessToken: !!accessToken, 
          hasRefreshToken: !!refreshToken,
          accessTokenPreview: accessToken ? accessToken.substring(0, 20) + '...' : 'none'
        });
        console.log('🔐 [AuthService] Extracted user:', user);
        
        // Store tokens
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
          console.log('✅ [AuthService] accessToken saved to localStorage');
        } else {
          console.warn('⚠️ [AuthService] No accessToken in response!');
        }
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
          console.log('✅ [AuthService] refreshToken saved to localStorage');
        } else {
          console.warn('⚠️ [AuthService] No refreshToken in response!');
        }
        // Store user data
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          console.log('✅ [AuthService] user saved to localStorage:', user);
        } else {
          console.warn('⚠️ [AuthService] No user in response!');
        }
        
        // Verify localStorage was set
        console.log('🔍 [AuthService] Verifying localStorage:');
        console.log('  - accessToken:', localStorage.getItem('accessToken') ? 'SET' : 'NOT SET');
        console.log('  - refreshToken:', localStorage.getItem('refreshToken') ? 'SET' : 'NOT SET');
        console.log('  - user:', localStorage.getItem('user') ? 'SET' : 'NOT SET');
        
        // Transform response to match expected format
        const transformedResponse = {
          status: 'success',
          data: {
            user,
            tokens: {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
          },
        };
        
        console.log('🔐 [AuthService] Returning transformed response:', transformedResponse);
        return transformedResponse;
      }
      
      console.warn('⚠️ [AuthService] Response not successful or missing data:', response);
      return response;
    } catch (error) {
      console.error('❌ [AuthService] Login error:', error);
      console.error('❌ [AuthService] Error response:', error.response);
      console.error('❌ [AuthService] Error message:', error.message);
      throw error;
    }
  },

  // Register
  register: async (userData) => {
    return await axiosInstance.post(API_ENDPOINTS.AUTH.REGISTER, userData);
  },

  // Logout
  logout: async () => {
    try {
      await axiosInstance.post(API_ENDPOINTS.AUTH.LOGOUT);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },

  // Get current user
  getCurrentUser: async () => {
    return await axiosInstance.get(API_ENDPOINTS.AUTH.ME);
  },

  // Forgot password
  forgotPassword: async (email) => {
    return await axiosInstance.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
  },

  // Reset password
  resetPassword: async (token, newPassword) => {
    return await axiosInstance.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      token,
      newPassword,
    });
  },

  // Refresh token
  refreshToken: async (refreshToken) => {
    const response = await axiosInstance.post(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });
    
    if (response && response.status === 'success' && response.data) {
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      
      if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
      }
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
    }
    
    return response;
  },
};

export default authService;
